import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { formatDate, num } from "../lib/format";
import { Receipt, CheckCircle, XCircle, Printer, ArrowLeft, FileText, CurrencyDollar, Calendar, Buildings } from "@phosphor-icons/react";

const TABS = [
  { key: "pending",  label: "Pending",  testid: "invoicing-tab-pending" },
  { key: "invoices", label: "Invoices", testid: "invoicing-tab-invoices" },
];

const STATUS_CHIPS = {
  issued:    { label: "Issued",    cls: "bg-sky-50 text-ps-blue border-sky-200" },
  paid:      { label: "Paid",      cls: "bg-lime-50 text-lime-700 border-lime-200" },
  cancelled: { label: "Cancelled", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

const fmtMoney = (amount, currency) => {
  if (amount == null || isNaN(amount)) return "—";
  const symbol = { GHS: "GH₵", XOF: "CFA", USD: "US$", EUR: "€" }[currency] || (currency + " ");
  return `${symbol} ${num(amount, 2)}`;
};

export default function Invoicing() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(routeId ? "invoices" : "pending");
  const [pendingGroups, setPendingGroups] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});           // { activityId: true } per group
  const [taxPct, setTaxPct] = useState({});               // per group
  const [creating, setCreating] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [detailInvoice, setDetailInvoice] = useState(null);

  const loadPending = async () => {
    const { data } = await api.get("/invoicing/pending");
    setPendingGroups(data || []);
  };
  const loadInvoices = async () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    const { data } = await api.get("/invoices", { params });
    setInvoices(data || []);
  };

  useEffect(() => {
    let alive = true;
    const run = async () => {
      setLoading(true);
      try {
        if (tab === "pending") await loadPending();
        else await loadInvoices();
      } finally { if (alive) setLoading(false); }
    };
    run();
    return () => { alive = false; };
  }, [tab, statusFilter]);

  // Deep-link to a specific invoice via /invoicing/:id
  useEffect(() => {
    if (routeId) {
      api.get(`/invoices/${routeId}`).then(({ data }) => {
        setDetailInvoice(data);
        setTab("invoices");
      }).catch(() => navigate("/invoicing", { replace: true }));
    }
  }, [routeId, navigate]);

  const toggleActivity = (gKey, activityId) => {
    setSelected((prev) => {
      const next = { ...prev };
      const key = `${gKey}::${activityId}`;
      if (next[key]) delete next[key]; else next[key] = true;
      return next;
    });
  };

  const toggleGroupAll = (gKey, group) => {
    setSelected((prev) => {
      const next = { ...prev };
      const allKeys = group.activities.map((a) => `${gKey}::${a.id}`);
      const allSelected = allKeys.every((k) => next[k]);
      if (allSelected) allKeys.forEach((k) => delete next[k]);
      else allKeys.forEach((k) => { next[k] = true; });
      return next;
    });
  };

  const selectedForGroup = (gKey, group) => {
    return group.activities.filter((a) => selected[`${gKey}::${a.id}`]).map((a) => a.id);
  };

  const groupSubtotal = (gKey, group) => {
    return group.activities
      .filter((a) => selected[`${gKey}::${a.id}`])
      .reduce((s, a) => s + (a.billable_amount || 0), 0);
  };

  const createInvoice = async (gKey, group) => {
    const ids = selectedForGroup(gKey, group);
    if (!ids.length) { window.alert("Select at least one activity."); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/invoices", {
        activity_ids: ids,
        tax_pct: parseFloat(taxPct[gKey] || 0) || 0,
      });
      setSelected((prev) => {
        const next = { ...prev };
        ids.forEach((id) => delete next[`${gKey}::${id}`]);
        return next;
      });
      setTaxPct((prev) => ({ ...prev, [gKey]: "" }));
      await loadPending();
      window.alert(`Invoice ${data.invoice_number} created — total ${fmtMoney(data.total, data.currency)}`);
    } catch (e) {
      window.alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCreating(false);
    }
  };

  const openInvoice = async (inv) => {
    const { data } = await api.get(`/invoices/${inv.id}`);
    setDetailInvoice(data);
  };

  const markPaid = async (inv) => {
    if (!window.confirm(`Mark invoice ${inv.invoice_number} as paid?`)) return;
    try {
      const { data } = await api.post(`/invoices/${inv.id}/mark-paid`);
      setDetailInvoice(data);
      await loadInvoices();
    } catch (e) {
      window.alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const cancelInvoice = async (inv) => {
    if (!window.confirm(`Cancel invoice ${inv.invoice_number}? Linked activities will return to the Pending list and can be re-invoiced.`)) return;
    try {
      const { data } = await api.post(`/invoices/${inv.id}/cancel`);
      setDetailInvoice(data);
      await loadInvoices();
    } catch (e) {
      window.alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const totalPending = useMemo(() => pendingGroups.reduce((s, g) => s + g.subtotal, 0), [pendingGroups]);
  const groupsByCurrency = useMemo(() => {
    const m = {};
    pendingGroups.forEach((g) => { m[g.currency] = (m[g.currency] || 0) + g.subtotal; });
    return m;
  }, [pendingGroups]);

  if (detailInvoice) {
    return <InvoiceDetail
      invoice={detailInvoice}
      onClose={() => { setDetailInvoice(null); if (routeId) navigate("/invoicing", { replace: true }); }}
      onMarkPaid={() => markPaid(detailInvoice)}
      onCancel={() => cancelInvoice(detailInvoice)}
    />;
  }

  return (
    <div data-testid="invoicing-page">
      <PageHeader subtitle="Billing" title="Invoicing" />

      <div className="px-8 py-4 bg-slate-100 border-b border-slate-200 flex flex-wrap gap-3 items-end">
        <div className="flex border border-slate-300 rounded-md overflow-hidden">
          {TABS.map((t) => (
            <button key={t.key} data-testid={t.testid} onClick={() => setTab(t.key)} className={`px-4 py-2 text-xs uppercase tracking-[0.2em] font-bold transition-colors ${tab === t.key ? "bg-ps-blue text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>{t.label}</button>
          ))}
        </div>
        {tab === "invoices" && (
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="invoicing-status-filter" className="border border-slate-300 rounded-md bg-white px-2 py-2 text-sm">
              <option value="">All</option>
              <option value="issued">Issued</option>
              <option value="paid">Paid</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        )}
        {tab === "pending" && (
          <div className="ml-auto flex flex-wrap gap-x-8 gap-y-2">
            <Stat label="Total ready" value={Object.entries(groupsByCurrency).map(([c, v]) => fmtMoney(v, c)).join(" · ") || "—"} accent="text-ps-deep" />
            <Stat label="Client groups" value={pendingGroups.length} accent="text-ps-blue" />
          </div>
        )}
      </div>

      <div className="p-8 space-y-6">
        {loading && <div className="brand-progress w-1/3 mx-auto" />}
        {!loading && tab === "pending" && pendingGroups.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center" data-testid="invoicing-empty">
            <Receipt size={36} className="mx-auto text-slate-300 mb-2" />
            <div className="font-display font-extrabold text-xl text-ps-deep">Nothing to invoice</div>
            <div className="text-sm text-slate-500 mt-1">Activities become ready to invoice once their trips_done is reconciled with a billing rate.</div>
          </div>
        )}

        {!loading && tab === "pending" && pendingGroups.map((g) => {
          const gKey = `${g.client}__${g.currency}`;
          const selIds = selectedForGroup(gKey, g);
          const subtotal = groupSubtotal(gKey, g);
          const allSelected = selIds.length === g.activities.length && g.activity_count > 0;
          return (
            <div key={gKey} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`invoicing-group-${g.client}-${g.currency}`}>
              <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
                <div className="w-10 h-10 grid place-items-center rounded-md bg-sky-50 text-ps-blue">
                  <Buildings size={20} weight="duotone" />
                </div>
                <div className="flex-1">
                  <div className="font-heading font-black text-lg tracking-tight text-ps-deep">{g.client}</div>
                  <div className="text-[11px] text-slate-500">{g.activity_count} activit{g.activity_count === 1 ? "y" : "ies"} ready · subtotal {fmtMoney(g.subtotal, g.currency)}</div>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Tax %</label>
                  <input
                    type="number" step="0.01" min="0"
                    value={taxPct[gKey] || ""}
                    onChange={(e) => setTaxPct({ ...taxPct, [gKey]: e.target.value })}
                    data-testid={`invoicing-tax-${g.client}-${g.currency}`}
                    placeholder="0"
                    className="w-20 border border-slate-300 px-2 py-1 text-sm font-mono-data"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => createInvoice(gKey, g)}
                  disabled={creating || selIds.length === 0}
                  data-testid={`invoicing-create-${g.client}-${g.currency}`}
                  className="inline-flex items-center gap-2 bg-ps-blue text-white px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] rounded-md hover:bg-ps-deep disabled:opacity-50"
                >
                  <Receipt size={14} weight="bold" /> Invoice {selIds.length} · {fmtMoney(subtotal, g.currency)}
                </button>
              </div>
              <div className="overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-600 border-b border-slate-200">
                    <tr>
                      <th className="px-3 py-2 text-left">
                        <input type="checkbox" checked={allSelected} onChange={() => toggleGroupAll(gKey, g)} data-testid={`invoicing-select-all-${g.client}-${g.currency}`} />
                      </th>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Truck · Driver</th>
                      <th className="px-3 py-2 text-left">Description</th>
                      <th className="px-3 py-2 text-right">Mode</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2 text-right">Rate</th>
                      <th className="px-3 py-2 text-right">Waiting</th>
                      <th className="px-3 py-2 text-right">Billable</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {g.activities.map((a) => {
                      const key = `${gKey}::${a.id}`;
                      const checked = !!selected[key];
                      const qty = a.billing_mode === "per_tonne" ? a.tonnes_actual : a.trips_done;
                      const qtyLabel = a.billing_mode === "per_tonne" ? `${num(qty, 2)} t` : `${qty} trip${qty === 1 ? "" : "s"}`;
                      return (
                        <tr key={a.id} className="hover:bg-slate-50" data-testid={`invoicing-activity-${a.id}`}>
                          <td className="px-3 py-2">
                            <input type="checkbox" checked={checked} onChange={() => toggleActivity(gKey, a.id)} data-testid={`invoicing-check-${a.id}`} />
                          </td>
                          <td className="px-3 py-2 font-mono-data text-xs whitespace-nowrap">{formatDate(a.date)}</td>
                          <td className="px-3 py-2">
                            <div className="font-mono-data text-ps-blue font-semibold text-xs">{a.vehicle_number}</div>
                            <div className="text-[11px] text-slate-500">{a.driver_name || "—"}</div>
                          </td>
                          <td className="px-3 py-2 text-xs">
                            <div className="capitalize font-semibold">{a.activity_type}</div>
                            <div className="text-slate-500 truncate max-w-[200px]" title={a.activity_detail}>{a.activity_detail || "—"}</div>
                          </td>
                          <td className="px-3 py-2 text-right text-[11px] capitalize">{(a.billing_mode || "per_trip").replace("_", " ")}</td>
                          <td className="px-3 py-2 text-right font-mono-data tabular">{qtyLabel}</td>
                          <td className="px-3 py-2 text-right font-mono-data tabular">{fmtMoney(a.rate_per_unit_snapshot, a.rate_currency_snapshot)}</td>
                          <td className="px-3 py-2 text-right font-mono-data tabular text-[11px]">
                            {a.waiting_days_actual ? `${a.waiting_days_actual} d · ${fmtMoney(a.waiting_days_actual * a.waiting_day_rate_snapshot, a.rate_currency_snapshot)}` : "—"}
                          </td>
                          <td className="px-3 py-2 text-right font-mono-data tabular font-bold text-lime-700">{fmtMoney(a.billable_amount, a.rate_currency_snapshot)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}

        {!loading && tab === "invoices" && (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-600 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-3 text-left">Number</th>
                    <th className="px-3 py-3 text-left">Client</th>
                    <th className="px-3 py-3 text-left">Period</th>
                    <th className="px-3 py-3 text-right">Lines</th>
                    <th className="px-3 py-3 text-right">Subtotal</th>
                    <th className="px-3 py-3 text-right">Tax</th>
                    <th className="px-3 py-3 text-right">Total</th>
                    <th className="px-3 py-3 text-left">Status</th>
                    <th className="px-3 py-3 text-right">Issued</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-testid="invoicing-invoices-table">
                  {invoices.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-slate-500">No invoices yet.</td></tr>}
                  {invoices.map((inv) => {
                    const chip = STATUS_CHIPS[inv.status] || STATUS_CHIPS.issued;
                    return (
                      <tr key={inv.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openInvoice(inv)} data-testid={`invoice-row-${inv.invoice_number}`}>
                        <td className="px-3 py-2 font-mono-data text-ps-blue font-bold">{inv.invoice_number}</td>
                        <td className="px-3 py-2 font-semibold">{inv.client}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{inv.period_start} → {inv.period_end}</td>
                        <td className="px-3 py-2 text-right font-mono-data tabular">{inv.lines.length}</td>
                        <td className="px-3 py-2 text-right font-mono-data tabular">{fmtMoney(inv.subtotal, inv.currency)}</td>
                        <td className="px-3 py-2 text-right font-mono-data tabular">{inv.tax_amount ? fmtMoney(inv.tax_amount, inv.currency) : "—"}</td>
                        <td className="px-3 py-2 text-right font-mono-data tabular font-bold text-ps-deep">{fmtMoney(inv.total, inv.currency)}</td>
                        <td className="px-3 py-2"><span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${chip.cls}`}>{chip.label}</span></td>
                        <td className="px-3 py-2 text-right text-xs text-slate-600 font-mono-data">{formatDate(inv.issued_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InvoiceDetail({ invoice, onClose, onMarkPaid, onCancel }) {
  const chip = STATUS_CHIPS[invoice.status] || STATUS_CHIPS.issued;
  const doPrint = () => window.print();
  return (
    <div data-testid="invoice-detail">
      <div className="px-8 py-4 bg-white border-b border-slate-200 flex items-center gap-3 print:hidden">
        <button onClick={onClose} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-ps-deep" data-testid="invoice-back"><ArrowLeft size={16} /> Back</button>
        <div className="flex-1" />
        <button onClick={doPrint} className="inline-flex items-center gap-1.5 border border-slate-300 px-3 py-2 text-xs font-bold uppercase tracking-[0.2em] rounded-md hover:bg-slate-50" data-testid="invoice-print"><Printer size={14} /> Print</button>
        {invoice.status === "issued" && (
          <>
            <button onClick={onCancel} className="inline-flex items-center gap-1.5 border border-red-200 text-ps-red px-3 py-2 text-xs font-bold uppercase tracking-[0.2em] rounded-md hover:bg-red-50" data-testid="invoice-cancel"><XCircle size={14} /> Cancel</button>
            <button onClick={onMarkPaid} className="inline-flex items-center gap-1.5 bg-lime-600 text-white px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] rounded-md hover:bg-lime-700" data-testid="invoice-mark-paid"><CheckCircle size={14} /> Mark paid</button>
          </>
        )}
      </div>

      <div className="max-w-4xl mx-auto p-8 print:p-0">
        <div className="bg-white border border-slate-200 rounded-xl p-8 print:border-0 print:shadow-none">
          <div className="flex items-start justify-between mb-8">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-ps-blue font-semibold mb-1">Portside · Yard Operations</div>
              <h1 className="font-heading font-black text-4xl tracking-tight text-ps-deep">INVOICE</h1>
              <div className="text-sm text-slate-600 mt-1 font-mono-data">{invoice.invoice_number}</div>
            </div>
            <div className="text-right">
              <span className={`px-3 py-1 text-[11px] font-bold uppercase tracking-wider rounded border ${chip.cls}`}>{chip.label}</span>
              <div className="text-xs text-slate-500 mt-2 flex items-center gap-1.5 justify-end"><Calendar size={12} /> Issued {formatDate(invoice.issued_at)}</div>
              {invoice.due_at && <div className="text-xs text-slate-500 flex items-center gap-1.5 justify-end mt-0.5">Due {formatDate(invoice.due_at)}</div>}
              {invoice.paid_at && <div className="text-xs text-lime-700 font-semibold flex items-center gap-1.5 justify-end mt-0.5"><CheckCircle size={12} weight="bold" /> Paid {formatDate(invoice.paid_at)}</div>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8 mb-8">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Bill to</div>
              <div className="font-heading font-bold text-xl text-ps-deep">{invoice.client}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Period</div>
              <div className="font-mono-data text-sm text-ps-deep">{invoice.period_start} → {invoice.period_end}</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-3 mb-1">Currency</div>
              <div className="font-mono-data font-bold text-ps-deep flex items-center gap-1"><CurrencyDollar size={12} /> {invoice.currency}</div>
            </div>
          </div>

          <table className="w-full text-sm mb-6">
            <thead className="text-[10px] uppercase tracking-[0.2em] text-slate-600 border-b-2 border-ps-deep">
              <tr>
                <th className="px-2 py-2 text-left">Date</th>
                <th className="px-2 py-2 text-left">Description</th>
                <th className="px-2 py-2 text-left">Truck</th>
                <th className="px-2 py-2 text-right">Qty</th>
                <th className="px-2 py-2 text-right">Rate</th>
                <th className="px-2 py-2 text-right">Waiting</th>
                <th className="px-2 py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {invoice.lines.map((l) => (
                <tr key={l.activity_id}>
                  <td className="px-2 py-2 font-mono-data text-xs">{l.date}</td>
                  <td className="px-2 py-2">
                    <div className="text-xs text-slate-700">{l.description}</div>
                    {l.driver_name && <div className="text-[10px] text-slate-500">{l.driver_name}</div>}
                  </td>
                  <td className="px-2 py-2 font-mono-data text-ps-blue text-xs font-semibold">{l.vehicle_number}</td>
                  <td className="px-2 py-2 text-right font-mono-data tabular">{l.billing_mode === "per_tonne" ? `${num(l.qty, 2)} t` : `${l.qty} trip${l.qty === 1 ? "" : "s"}`}</td>
                  <td className="px-2 py-2 text-right font-mono-data tabular text-xs">{fmtMoney(l.rate, invoice.currency)}</td>
                  <td className="px-2 py-2 text-right font-mono-data tabular text-xs text-slate-600">{l.waiting_days ? `${l.waiting_days} d × ${fmtMoney(l.waiting_day_rate, invoice.currency)}` : "—"}</td>
                  <td className="px-2 py-2 text-right font-mono-data tabular font-semibold text-ps-deep">{fmtMoney(l.line_total, invoice.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex justify-end">
            <div className="w-72 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Subtotal</span>
                <span className="font-mono-data tabular font-semibold">{fmtMoney(invoice.subtotal, invoice.currency)}</span>
              </div>
              {invoice.tax_pct > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Tax ({invoice.tax_pct}%)</span>
                  <span className="font-mono-data tabular">{fmtMoney(invoice.tax_amount, invoice.currency)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg pt-2 border-t-2 border-ps-deep">
                <span className="font-heading font-bold text-ps-deep">Total</span>
                <span className="font-mono-data tabular font-black text-ps-deep" data-testid="invoice-total">{fmtMoney(invoice.total, invoice.currency)}</span>
              </div>
            </div>
          </div>

          {invoice.notes && (
            <div className="mt-8 border-t border-slate-200 pt-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1 flex items-center gap-1"><FileText size={11} /> Notes</div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap">{invoice.notes}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "text-ps-deep" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">{label}</div>
      <div className={`font-mono-data tabular text-lg ${accent}`}>{value}</div>
    </div>
  );
}
